/**
 * kw-lexicon (2026-10-25) — 「사람들이 공통으로 붙여 치는 수식어」 사전(순수 · DB 0).
 *
 * 왜 — Node 지시 「공통적으로 많이 검색하는 키워드가 훨씬 더 많다 — 파악해서 검색 시 걸리게 반영」.
 *   현장명·지역명 뒤에 붙는 꼬리(「분양가」「모델하우스」「입주자모집공고」「추가분담금」…)는 현장이 달라도 같은 말이 반복된다.
 *   이 파일은 그 꼬리의 «정적 기본 사전» 이다 — 채굴 데이터(naver_keyword_pool → v_keyword_modifiers)가 쌓이기 전에도
 *   배포 직후부터 수집기(kick-keywords deriveModifier · 조합 탐침)와 선택기(pickKickKeywords 단계 목록)가 쓰도록.
 *
 * 쓰는 법
 *   · KW_LEXICON — 정식 수식어(term) · 같은 뜻 변형(aliases) · 맞는 단계(stages) · 받칠 사실(needs).
 *   · canonicalModifier(s) — 꼬리 → 정식 수식어(변형 흡수). 사전에 없으면 null.
 *   · lexiconStageList(stage) — 단계별 정적 목록(정적 순서 · KICK_STAGE_MODIFIERS 확장분의 단일 출처).
 *   · mergeMinedModifiers(…) — 채굴 집계(v_keyword_modifiers)로 순서·발견 수식어를 더한다(측정 전용 · 페이지 노출은 정적 사전 + 사실 관문만).
 *
 * ⛔ 사실 관문 — needs 가 있는 수식어는 호출자가 그 사실 깃발(KickFacts)을 넘길 때만 제목·h2·FAQ 에 쓴다
 *    (lib/seo/kick-keywords modifierSupported). 새로 들인 수식어의 깃발은 «기본 false» — 호출자가 사실을 넘기기 전엔 노출 0.
 * ⛔ 검색량 수치는 여기 두지 않는다(측정값은 API → DB 에만).
 */

export type LexStage = 'subscription' | 'upcoming' | 'redev' | 'unsold';

/** 수식어를 받칠 사실(kick-keywords KickFacts 의 키와 같은 이름). */
export type LexNeed =
  | 'price' | 'modelHouse' | 'schedule' | 'competition'
  | 'notice' | 'types' | 'location' | 'redevStage' | 'unsoldDetail' | 'moveIn' | 'trade';

export interface LexEntry {
  /** 정식 수식어(공백 없음) */
  term: string;
  /** 같은 뜻 변형(공백 없음 · 2자 이상) */
  aliases?: readonly string[];
  /** 이 수식어가 맞는 단계(없으면 일반 주제 — 페이지 수식어로 안 쓴다) */
  stages?: readonly LexStage[];
  /** 받칠 사실(없으면 관문 없음) */
  needs?: LexNeed;
}

/**
 * 정적 기본 사전 — 신축·분양·정비 검색에서 현장명/지역명 뒤에 반복해 붙는 꼬리(지시 목록 그대로 + 기존 KICK_MODIFIERS).
 * 순서 = 같은 단계 안 정적 우선순위(측정이 없을 때).
 */
export const KW_LEXICON: readonly LexEntry[] = [
  { term: '분양가', stages: ['subscription', 'upcoming', 'unsold'], needs: 'price' },
  { term: '모델하우스', aliases: ['견본주택'], stages: ['subscription', 'upcoming', 'unsold'], needs: 'modelHouse' },
  { term: '청약', stages: ['subscription', 'upcoming'] },
  { term: '청약일정', stages: ['subscription', 'upcoming'], needs: 'schedule' },
  { term: '분양일정', stages: ['upcoming', 'redev'], needs: 'schedule' },
  { term: '경쟁률', aliases: ['청약경쟁률'], stages: ['subscription'], needs: 'competition' },
  { term: '당첨자발표', aliases: ['당첨발표'], stages: ['subscription'], needs: 'schedule' },
  { term: '당첨가점', aliases: ['커트라인', '당첨커트라인', '가점커트라인'], stages: ['subscription'], needs: 'competition' },
  { term: '입주자모집공고', aliases: ['모집공고'], stages: ['subscription', 'upcoming'], needs: 'notice' },
  { term: '평면도', stages: ['subscription', 'upcoming'] },
  { term: '타입', aliases: ['평형'], stages: ['subscription', 'upcoming'], needs: 'types' },
  { term: '시세', stages: ['upcoming', 'redev', 'unsold'] },
  { term: '실거래가', stages: ['redev'], needs: 'trade' },
  { term: '분양권', stages: ['redev'] },
  { term: '프리미엄', stages: ['redev'], needs: 'trade' },
  { term: '전매제한', stages: ['subscription', 'upcoming'], needs: 'notice' },
  { term: '실거주의무', stages: ['subscription', 'upcoming'], needs: 'notice' },
  { term: '중도금대출', aliases: ['중도금'], stages: ['subscription', 'unsold'], needs: 'notice' },
  { term: '계약금', stages: ['subscription', 'unsold'], needs: 'notice' },
  { term: '옵션', aliases: ['발코니확장', '유상옵션'], stages: ['subscription', 'unsold'], needs: 'notice' },
  { term: '입주', stages: ['subscription', 'unsold'] },
  { term: '입주예정일', aliases: ['입주시기', '입주일'], stages: ['unsold'], needs: 'moveIn' },
  { term: '사전점검', stages: ['unsold'], needs: 'moveIn' },
  { term: '위치', stages: ['upcoming', 'redev'] },
  { term: '학군', stages: ['upcoming'], needs: 'location' },
  { term: '교통', stages: ['upcoming'], needs: 'location' },
  { term: '조감도', stages: ['upcoming'], needs: 'notice' },
  { term: '커뮤니티', stages: ['upcoming'], needs: 'notice' },
  { term: '무순위', aliases: ['줍줍'], stages: ['subscription', 'unsold'] },
  { term: '잔여세대', aliases: ['잔여'], stages: ['unsold'], needs: 'unsoldDetail' },
  { term: '선착순', aliases: ['선착순분양'], stages: ['unsold'], needs: 'unsoldDetail' },
  { term: '미분양', stages: ['unsold'] },
  { term: '할인분양', stages: ['unsold'], needs: 'unsoldDetail' },
  { term: '일반분양', stages: ['redev'] },
  { term: '조합원분양가', stages: ['redev'], needs: 'redevStage' },
  { term: '추가분담금', aliases: ['분담금'], stages: ['redev'], needs: 'redevStage' },
  { term: '관리처분', aliases: ['관리처분인가'], stages: ['redev'], needs: 'redevStage' },
  { term: '이주', aliases: ['이주비'], stages: ['redev'], needs: 'redevStage' },
  { term: '철거', stages: ['redev'], needs: 'redevStage' },
  { term: '시공사', aliases: ['시공사선정'], stages: ['redev'], needs: 'redevStage' },
  { term: '사업시행인가', stages: ['redev'], needs: 'redevStage' },
  { term: '진행상황', stages: ['redev'], needs: 'redevStage' },
  { term: '재개발', stages: ['redev'] },
  { term: '재건축', stages: ['redev'] },
  // 아래는 기존 KICK_MODIFIERS 에만 있던 꼬리(호환 — 수집기가 이미 이 이름으로 적재한다).
  { term: '당첨', stages: ['subscription'], needs: 'competition' },
];

/**
 * 일반 주제어(지역·현장 없이 단독 검색 · 페이지 «수식어» 가 아니라 가이드/허브 주제). 채굴 시드·관련도 판정에 쓴다.
 * 지시 목록 그대로(공백 없음).
 */
export const KW_TOPIC_TERMS: readonly string[] = [
  '청약', '분양', '아파트분양', '분양예정', '미분양', '무순위', '줍줍', '잔여세대', '선착순분양', '모델하우스', '견본주택',
  '입주자모집공고', '특별공급', '생애최초', '신혼부부특공', '청약가점', '청약통장', '당첨자발표', '경쟁률', '분양권', '전매제한',
  '실거주의무', '중도금대출', '계약금', '발코니확장', '입주예정아파트', '사전점검', '재개발', '재건축', '관리처분', '사업시행인가',
  '조합원분양가', '추가분담금', '이주비', '시공사선정', '가로주택정비', '지역주택조합', '신축아파트', '아파트시세', '실거래가',
  '취득세', '양도세', '중개수수료', 'DSR', '전월세전환', '청약가점계산기', '취득세계산기', '중개수수료계산기', '분양가상한제',
  '특별공급자격', '청약1순위', '오피스텔분양', '아파트청약',
];

/** 지역 × 주제 꼬리(지시 정본). */
export const KW_REGION_TOPICS: readonly string[] = [
  '청약', '아파트분양', '분양예정', '미분양', '재개발', '재건축', '신축아파트', '입주예정아파트', '모델하우스',
];

/**
 * 관련도 판정용 도메인 낱말 — 하나라도 품으면 신축·분양·정비·주택 거래 검색으로 본다.
 * (사전 term·alias 는 자동 포함)
 */
export const KW_DOMAIN_WORDS: readonly string[] = [
  '아파트', '분양', '청약', '주택', '부동산', '재개발', '재건축', '입주', '신축', '오피스텔', '집값', '시세', '매매', '전세', '월세',
  '대출', '취득세', '양도세', '중개', '수수료', 'dsr', 'ltv', 'dti', '특공', '특별공급', '당첨', '모집', '공고', '정비', '조합',
  '미분양', '줍줍', '분양권', '전매', '실거래', '타운하우스', '주상복합', '시공사', '이주', '철거', '계약금', '중도금', '잔금',
  '등기', '임대', '견본주택', '모델하우스', '가점', '통장', '생애최초', '신혼', '단지', '평형', '평면', '세대', '역세권', '학군',
];

/* ───────────────────────────── 색인 ───────────────────────────── */

const norm = (s: string | null | undefined) => String(s ?? '').replace(/\s+/g, '').toLowerCase();

/** 변형/정식 → 정식. */
const CANON: ReadonlyMap<string, string> = (() => {
  const m = new Map<string, string>();
  for (const e of KW_LEXICON) {
    m.set(norm(e.term), e.term);
    for (const a of e.aliases ?? []) if (norm(a).length >= 2 && !m.has(norm(a))) m.set(norm(a), e.term);
  }
  return m;
})();

const BY_TERM: ReadonlyMap<string, LexEntry> = new Map(KW_LEXICON.map((e) => [e.term, e]));

/** 사전 낱말 전부(정식 + 변형, 길이 내림) — 꼬리 찾기용. */
export const LEXICON_WORDS: readonly string[] = [...CANON.keys()].sort((a, b) => b.length - a.length);

/** 정식 수식어 전부(사전 순서). */
export const LEXICON_TERMS: readonly string[] = KW_LEXICON.map((e) => e.term);

/** 사전 항목. */
export function lexEntry(term: string): LexEntry | null {
  return BY_TERM.get(CANON.get(norm(term)) ?? '') ?? null;
}

/** 꼬리 → 정식 수식어(정확 일치 · 변형 흡수). 없으면 null. */
export function canonicalModifier(s: string | null | undefined): string | null {
  return CANON.get(norm(s)) ?? null;
}

/** 수식어가 받칠 사실(없으면 null). */
export function lexNeedOf(term: string): LexNeed | null {
  return lexEntry(term)?.needs ?? null;
}

/** 단계별 정적 목록(사전 순서). */
export function lexiconStageList(stage: LexStage): string[] {
  return KW_LEXICON.filter((e) => (e.stages ?? []).includes(stage)).map((e) => e.term);
}

/** 관련 검색어인가(도메인 낱말·사전 낱말 포함). */
export function isDomainKeyword(keyword: string): boolean {
  const k = norm(keyword);
  if (k.length < 2) return false;
  return KW_DOMAIN_WORDS.some((w) => k.includes(w)) || LEXICON_WORDS.some((w) => w.length >= 2 && k.includes(w));
}

/* ───────────────────────────── 채굴 결합 ───────────────────────────── */

/** v_keyword_modifiers 한 행(채굴 집계). */
export interface MinedModifier {
  modifier: string;
  known: boolean;
  keywords: number;
  contexts: number;
  total_volume: number | null;
  avg_volume: number | null;
}

export interface MergedModifier {
  term: string;
  /** 정적 사전에 있는가(있으면 사실 관문 아래 페이지 노출 가능 · 없으면 측정·보고 전용) */
  known: boolean;
  /** 채굴 평균 검색량(없으면 null — 정적 순서) */
  avgVolume: number | null;
  contexts: number;
}

/**
 * 정적 사전 + 채굴 집계 → 한 목록.
 *   · 정적 항목은 전부 남는다(데이터가 없어도 배포 직후 동작).
 *   · 채굴 발견어(사전 밖)는 contexts ≥ minContexts 이고 평균 검색량 ≥ minAvg 일 때만 붙는다(우연 한 번은 버림).
 *   · 순서: 채굴 평균 검색량 내림(측정 있는 것 먼저) → 정적 순서.
 */
export function mergeMinedModifiers(
  mined: readonly MinedModifier[],
  opts: { minContexts?: number; minAvg?: number; maxDiscovered?: number } = {},
): MergedModifier[] {
  const minContexts = opts.minContexts ?? 3;
  const minAvg = opts.minAvg ?? 30;
  const maxDiscovered = opts.maxDiscovered ?? 30;
  const stat = new Map<string, MinedModifier>();
  for (const m of mined) {
    const key = canonicalModifier(m.modifier) ?? norm(m.modifier);
    if (!key) continue;
    const prev = stat.get(key);
    if (!prev || (m.total_volume ?? 0) > (prev.total_volume ?? 0)) stat.set(key, { ...m, modifier: key });
  }
  const out: MergedModifier[] = LEXICON_TERMS.map((t) => {
    const s = stat.get(t);
    return { term: t, known: true, avgVolume: s?.avg_volume ?? null, contexts: s?.contexts ?? 0 };
  });
  const discovered = [...stat.values()]
    .filter((s) => !BY_TERM.has(s.modifier) && s.contexts >= minContexts && (s.avg_volume ?? 0) >= minAvg && isCleanModifier(s.modifier))
    .sort((a, b) => (b.total_volume ?? 0) - (a.total_volume ?? 0))
    .slice(0, maxDiscovered)
    .map((s) => ({ term: s.modifier, known: false, avgVolume: s.avg_volume, contexts: s.contexts }));
  const idx = new Map(LEXICON_TERMS.map((t, i) => [t, i]));
  return [...out, ...discovered].sort((a, b) => {
    const av = a.avgVolume ?? -1;
    const bv = b.avgVolume ?? -1;
    if (av !== bv) return bv - av;
    return (idx.get(a.term) ?? 999) - (idx.get(b.term) ?? 999);
  });
}

/** 발견 수식어 모양 검사 — 한글 2~10자(숫자·영문 섞인 단지 코드·URL 조각은 버림). */
export function isCleanModifier(s: string): boolean {
  return /^[가-힣]{2,10}$/.test(s);
}

/**
 * 단계 목록을 채굴 순서로 다시 줄 세운다(목록 밖은 넣지 않는다 — 페이지 노출 범위는 정적 단계 목록이 정한다).
 *   측정 평균이 있는 것 먼저(내림) → 원래 순서.
 */
export function orderByMined(list: readonly string[], merged: readonly MergedModifier[]): string[] {
  const vol = new Map(merged.map((m) => [m.term, m.avgVolume]));
  return [...list]
    .map((t, i) => ({ t, i, v: vol.get(canonicalModifier(t) ?? t) ?? null }))
    .sort((a, b) => (b.v ?? -1) - (a.v ?? -1) || a.i - b.i)
    .map((x) => x.t);
}
